import {parseNumber} from '@benchling/js-utils';
import reactUpdate from 'immutability-helper';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as SearchDAO from 'dao/SearchDAO';
import {SchemaTag} from 'folder-items/FileConstants';
import {
  ColumnDefinition,
  getColumnDefinitionIndex,
  SchemaFieldColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import * as ast from 'notebook/tables/excess/AST';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import LookupField from 'notebook/tables/excess/functions/lookupField';
import parser from 'notebook/tables/excess/Parser';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {ExternalCache, LookupFieldResultCached} from 'notebook/tables/lookup-field/LookupFieldCacheConstants';
import {
  LOOKUP_FIELD_FIELD_TYPE_EXP_COND,
  parseLookupSpecifierFromLookupFieldNode,
  STAGE_RUN_LOOKUP_FIELD_FIELD_TYPES,
} from 'notebook/tables/lookup-field/LookupFieldDefinitionParser';
import {Cell, RowData, Table, TableColumnType} from 'notebook/tables/TableConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import notifications from 'notifications';
import {FieldType, isNumericField} from 'schemas/fields/SchemaFieldConstants';
import {withLoadingScreen} from 'util/Promises';
import {WorkflowSelector} from 'workflows/reduceWorkflows';

import {
  BaseLookupDefinition,
  InputSampleLookupSpecifier,
  LookupFieldDefinition,
  LookupFieldResult,
  LookupSpecifier,
  SampleSpecifier,
  ServerLookupAggregatedQuery,
  ServerLookupAggregatedResult,
  ServerLookupResult,
  SubqueryLookupResultValue,
} from './LookupFieldDefinitionConstants';

/**
 * Takes rowData or lookupDefinition that includes sample specifier fields and returns
 * the fields that identify the sample.
 */
export function pickSampleSpecifier(objectWithSampleSpecifier: SampleSpecifier): SampleSpecifier {
  const {batchId, fileId, stageRunId, stageRunSampleId} = objectWithSampleSpecifier;
  return {
    batchId: batchId || null,
    fileId: fileId || null,
    stageRunId: stageRunId || null,
    stageRunSampleId: stageRunSampleId || null,
  };
}

function normalizeSampleSpecifier(objectWithSampleSpecifier: SampleSpecifier): SampleSpecifier {
  return {
    // Defaults for sample specifier values for the case they are not defined in the input object
    batchId: null,
    fileId: null,
    stageRunSampleId: null,
    stageRunId: null,
    ...pickSampleSpecifier(objectWithSampleSpecifier),
  };
}

function pickLookupFieldDefinition(
  objectWithLookupFieldDefinition: BaseLookupDefinition & SampleSpecifier
): BaseLookupDefinition & SampleSpecifier {
  const {schemaName, schemaId, fieldName, fieldType, filterSpecifiers} = objectWithLookupFieldDefinition;
  return {
    schemaName: schemaName || null,
    schemaId: schemaId || null,
    fieldName,
    fieldType: fieldType || null,
    filterSpecifiers,
    ...pickSampleSpecifier(objectWithLookupFieldDefinition),
  };
}

function normalizeLookupFieldDefinition(
  lookupFieldDefinition: (LookupSpecifier | InputSampleLookupSpecifier) & SampleSpecifier
): BaseLookupDefinition & SampleSpecifier {
  // Decide if we should use the stage run or stage run sample (or batch, or file).
  // To remain compatible with lookup specifiers cached from before stageRunId was added,
  // we need to set stageRunId to null when normalizing lookup definitions
  // that aren't referencing a stage run.
  let isStageRunLookup = false;
  if (lookupFieldDefinition.stageRunId != null) {
    let innermostSpecifier = lookupFieldDefinition;
    while (innermostSpecifier.sourceSpecifier != null) {
      innermostSpecifier = innermostSpecifier.sourceSpecifier.lookupSpecifier;
    }
    if (STAGE_RUN_LOOKUP_FIELD_FIELD_TYPES.includes(innermostSpecifier.fieldType!)) {
      isStageRunLookup = true;
    }
  }

  let normalizedLookupFieldDefinition = {
    ...pickLookupFieldDefinition(lookupFieldDefinition),
    ...normalizeSampleSpecifier(lookupFieldDefinition),
    filterSpecifiers:
      lookupFieldDefinition.filterSpecifiers == null ? [] : lookupFieldDefinition.filterSpecifiers,
  };
  if (isStageRunLookup) {
    normalizedLookupFieldDefinition = {
      ...normalizedLookupFieldDefinition,
      batchId: null,
      fileId: null,
      stageRunSampleId: null,
    };
  } else {
    normalizedLookupFieldDefinition = {
      ...normalizedLookupFieldDefinition,
      stageRunId: null,
    };
  }
  return normalizedLookupFieldDefinition;
}

function normalizeLookupSpecifier(lookupSpecifier: LookupSpecifier): LookupSpecifier {
  const {schemaName, schemaId, fieldType, fieldName, filterSpecifiers, sourceSpecifier} = lookupSpecifier;
  return {
    schemaName: schemaName || null,
    schemaId: schemaId || null,
    fieldType,
    fieldName,
    filterSpecifiers: filterSpecifiers == null ? [] : filterSpecifiers,
    sourceSpecifier:
      sourceSpecifier == null
        ? null
        : {
            ...sourceSpecifier,
            lookupSpecifier: normalizeLookupSpecifier(sourceSpecifier.lookupSpecifier),
          },
  };
}

function areSampleSpecifiersEqual(
  sampleSpecifier1: SampleSpecifier,
  sampleSpecifier2: SampleSpecifier
): boolean {
  return isEqual(normalizeSampleSpecifier(sampleSpecifier1), normalizeSampleSpecifier(sampleSpecifier2));
}

export function areLookupFieldDefinitionsEqual(
  lookupFieldDefinition1: LookupFieldDefinition | LookupSpecifier | InputSampleLookupSpecifier,
  lookupFieldDefinition2: LookupFieldDefinition | LookupSpecifier | InputSampleLookupSpecifier
): boolean {
  return isEqual(
    normalizeLookupFieldDefinition(lookupFieldDefinition1),
    normalizeLookupFieldDefinition(lookupFieldDefinition2)
  );
}

export function areLookupSpecifiersEqual(
  lookupFieldDefinition1: LookupSpecifier,
  lookupFieldDefinition2: LookupSpecifier
): boolean {
  return isEqual(
    normalizeLookupSpecifier(lookupFieldDefinition1),
    normalizeLookupSpecifier(lookupFieldDefinition2)
  );
}

function findIndexInCache({
  externalCache,
  lookupSpecifier,
  sampleSpecifier,
}: {
  externalCache: ExternalCache;
  lookupSpecifier: LookupSpecifier;
  sampleSpecifier: SampleSpecifier;
}): number {
  const lookupFieldDefinition = {
    ...lookupSpecifier,
    ...sampleSpecifier,
  };
  return externalCache.LOOKUPFIELD.findIndex(
    (cachedValue: LookupFieldResultCached) =>
      cachedValue != null && areLookupFieldDefinitionsEqual(cachedValue, lookupFieldDefinition)
  );
}

/**
 * Return the results from the external cache that match the specified lookupFieldDefinition.
 *
 * @param lookupSpecifier - should match the format of the LookupDefinition portion stored as
 *                          LookupFieldResultCached in the cache.
 *                          May contain extra properties. All the properties that are not
 *                          stored in external cache are ignored.
 * @param sampleSpecifier - should match the format of the SampleSpecifier portion stored as
 *                          LookupFieldResultCached in the cache.
 *                          May contain extra properties. All the properties that are not
 *                          stored in external cache are ignored.
 */
export function getLookupFieldResultsFromCache({
  externalCache,
  lookupSpecifier,
  sampleSpecifier,
}: {
  externalCache: ExternalCache | null;
  lookupSpecifier: LookupSpecifier;
  sampleSpecifier: SampleSpecifier;
}): Array<LookupFieldResult> | null {
  if (externalCache == null || lookupSpecifier == null || sampleSpecifier == null) {
    return null;
  }
  if (externalCache.LOOKUPFIELD == null) {
    return null;
  }

  const existingValueIndex = findIndexInCache({
    externalCache,
    lookupSpecifier,
    sampleSpecifier,
  });
  if (existingValueIndex < 0) {
    return null;
  }
  const existingValue = externalCache.LOOKUPFIELD[existingValueIndex];
  return existingValue == null ? null : existingValue.results;
}

export function getBatchId(workflowSelector: WorkflowSelector, rowData: RowData): string | null {
  if (rowData.stageRunSampleId != null) {
    const stageRunSample = workflowSelector.getStageRunSample(rowData.stageRunSampleId);
    return stageRunSample && stageRunSample.batch ? stageRunSample.batch.id : null;
  }
  if (rowData.batchId) {
    return rowData.batchId;
  }
  return null;
}

export function getFileId(workflowSelector: WorkflowSelector, rowData: RowData): string | null | undefined {
  if (rowData.stageRunSampleId != null) {
    const stageRunSample = workflowSelector.getStageRunSample(rowData.stageRunSampleId);
    return stageRunSample ? stageRunSample.fileId : null;
  }
  if (rowData.fileId) {
    return rowData.fileId;
  }
  return null;
}

function getValueType(tag: SchemaTag): string {
  if (tag.tagSchemaField != null && !isNumericField(tag.tagSchemaField)) {
    return ExcessUtil.VALUE_TYPES.STRING;
  }
  return tag.value == null || parseNumber(tag.value) == null
    ? ExcessUtil.VALUE_TYPES.STRING
    : ExcessUtil.VALUE_TYPES.NUMBER;
}

export function convertExterimentalConditionStringToTag(strValue: string, name: string): SchemaTag {
  return {
    name,
    value: strValue,
    // We are filling in bunch of dummy values. They don't matter as they are never
    // read. We shouldn't require SchemaTag and should only include the subset we care about
    tagSchemaField: {
      archiveRecord: null,
      id: -1,
      dbId: -1,
      apiId: 'N/A',
      name,
      displayName: name,
      systemName: name,
      fieldType: FieldType.TEXT,
      isRequired: false,
      isMulti: false,
      isComputed: false,
      isConvertedFromLink: false,
      isSnapshot: false,
      isIntegration: false,
      isParentLink: false,
      requiredLink: null,
    },
  };
}

/**
 * Parse a result of a single SubqueryLookupResultValue into the format we can store in the
 * external cache of the table cell or display in the Lookup Field Configuration modal.
 */
function subQueryResultValueToLookupFieldResult(
  subQueryResultValue: SubqueryLookupResultValue
): LookupFieldResult | null {
  const {tag} = subQueryResultValue;
  if (tag.value == null || tag.value.length === 0) {
    return null;
  }

  // eslint-disable-next-line no-restricted-syntax
  return _.extend(
    {
      // type and value fields are redundant, but kept for backwards compatibility
      type: getValueType(tag),
      value: tag.value,
      tagValue: tag,
    },
    subQueryResultValue.assayResultSummary && {assayResultSummary: subQueryResultValue.assayResultSummary},
    subQueryResultValue.entry && {entry: subQueryResultValue.entry}
  );
}

function getSampleSpecifiersFromResult(result: LookupFieldResult): Array<SampleSpecifier> {
  const sampleSpecifiers: Array<SampleSpecifier> = [];

  const targetFiles = result.tagValue == null ? null : result.tagValue.targetFiles;
  if (targetFiles != null) {
    for (const targetFile of targetFiles) {
      sampleSpecifiers.push({
        batchId: null,
        fileId: targetFile.id,
      });
    }
  }
  const targetBatches = result.tagValue == null ? null : result.tagValue.targetBatches;
  if (targetBatches != null) {
    for (const targetBatch of targetBatches) {
      sampleSpecifiers.push({
        batchId: targetBatch.id,
        fileId: null,
      });
    }
  }
  return sampleSpecifiers;
}

function areNotNullAndEqual(prop1: string | null | undefined, prop2: string | null | undefined): boolean {
  return prop1 != null && prop1 === prop2;
}

/**
 * Extract the final LookupFieldResults that match the lookupSpecifier and the topLevelSampleSpecifier
 * out from resolvedServerResults.
 *
 * `lookupSpecifier`         top level lookup specifier (potentially containing nested source specifiers)
 * `topLevelSampleSpecifier` the sample specifier or the top level sample. This would be the sample taken from
 *                           the rowData we are performing the query for.
 * `resolvedServerResults`   contains all the subquery results that are chained together to assemble the
 *                           final query results.
 */
export function resolveLookupSpecifierAndReturnFinalResults({
  lookupSpecifier,
  topLevelSampleSpecifier,
  resolvedServerResults,
}: {
  lookupSpecifier: LookupSpecifier;
  topLevelSampleSpecifier: SampleSpecifier;
  resolvedServerResults: Array<{
    subQueryLookupSpecifier: BaseLookupDefinition;
    subQueryLookupFieldResult: {
      lookupFieldDefinition: BaseLookupDefinition & SampleSpecifier;
      results: Array<LookupFieldResult>;
    };
  }>;
}): Array<LookupFieldResult> | null {
  let sampleSpecifiers: Array<SampleSpecifier>;
  if (lookupSpecifier.sourceSpecifier == null) {
    // Source is not specified - use the top level sample as the sample the results belong to
    sampleSpecifiers = [topLevelSampleSpecifier];
  } else {
    // Source is specified - resolve the source results first and use them as the samples the
    // current results belong to
    sampleSpecifiers = [];
    const sourceSampleResults = resolveLookupSpecifierAndReturnFinalResults({
      lookupSpecifier: lookupSpecifier.sourceSpecifier.lookupSpecifier,
      topLevelSampleSpecifier,
      resolvedServerResults,
    });
    if (sourceSampleResults == null) {
      return null;
    }
    for (const result of sourceSampleResults) {
      sampleSpecifiers = [...sampleSpecifiers, ...getSampleSpecifiersFromResult(result)];
    }
  }

  let results: Array<LookupFieldResult> = [];
  for (const sampleSpecifier of sampleSpecifiers) {
    for (const subQueryResult of resolvedServerResults) {
      const {subQueryLookupFieldResult} = subQueryResult;
      // We are not using areSampleSpecifiersEqual intentionally because sampleSpecifier may contain
      // both batchId/fileId and stageRunId, but only one of them would be present in
      // subQueryLookupFieldResult.lookupFieldDefinition.
      if (
        areNotNullAndEqual(
          sampleSpecifier.batchId,
          subQueryLookupFieldResult.lookupFieldDefinition.batchId
        ) ||
        areNotNullAndEqual(sampleSpecifier.fileId, subQueryLookupFieldResult.lookupFieldDefinition.fileId) ||
        areNotNullAndEqual(
          sampleSpecifier.stageRunId,
          subQueryLookupFieldResult.lookupFieldDefinition.stageRunId
        )
      ) {
        const lookupFieldDefinition = {
          ...lookupSpecifier,
          ...pickSampleSpecifier(subQueryLookupFieldResult.lookupFieldDefinition),
        };
        if (
          areLookupFieldDefinitionsEqual(
            lookupFieldDefinition,
            subQueryLookupFieldResult.lookupFieldDefinition
          )
        ) {
          results = [...results, ...subQueryLookupFieldResult.results];
        }
      }
    }
  }
  return results;
}

export function mergeResultIntoExternalCache(
  externalCache: ExternalCache,
  lookupFieldResults: LookupFieldResultCached
): ExternalCache {
  let newExternalCache: ExternalCache = externalCache || {LOOKUPFIELD: []};
  if (newExternalCache.LOOKUPFIELD == null) {
    newExternalCache.LOOKUPFIELD = [];
  }
  const existingValueIndex = newExternalCache.LOOKUPFIELD.findIndex(
    (cachedValue: LookupFieldResultCached) =>
      cachedValue != null && areLookupFieldDefinitionsEqual(cachedValue, lookupFieldResults)
  );
  if (existingValueIndex < 0) {
    newExternalCache = {
      ...newExternalCache,
      LOOKUPFIELD: [...newExternalCache.LOOKUPFIELD, lookupFieldResults],
    };
  } else {
    newExternalCache = {
      ...newExternalCache,
      LOOKUPFIELD: [
        ...newExternalCache.LOOKUPFIELD.slice(0, existingValueIndex),
        lookupFieldResults,
        ...newExternalCache.LOOKUPFIELD.slice(existingValueIndex + 1),
      ],
    };
  }

  return newExternalCache;
}

/**
 * Collect and return all the lookup specifiers from the specified node and its children recursively.
 *
 * @param  {ASTNode} node - the node to extract the lookup specifiers from. May be null
 * @return array of lookup specifiers when each item is from the shape:
 *         {schemaName, fieldName}
 */
function getAllLookupSpecifiersFromNode(node: ast.ASTNode): Array<LookupSpecifier> {
  if (node instanceof ast.FunctionOperator && node.type === LookupField.name) {
    const lookupSpecifier = parseLookupSpecifierFromLookupFieldNode(node);
    if (lookupSpecifier != null) {
      // No need to get specifiers from children since they are included in the
      // lookupSpecifier already
      return [lookupSpecifier];
    }
  }
  const lookupSpecifiers = [];
  if (node != null) {
    for (const child of node.childNodes()) {
      lookupSpecifiers.push(...getAllLookupSpecifiersFromNode(child));
    }
  }
  return lookupSpecifiers;
}

function getCellLookupSpecifiers(cell: Cell): Array<LookupSpecifier> {
  const node = parser.tryParse(cell.text);
  if (node) {
    return getAllLookupSpecifiersFromNode(node);
  }
  return [];
}

/**
 * Return stageRunId extracted from the table row.
 *
 * If stageRunId is present in the rowData - it is returned. Otherwise we are looking for a
 * STAGE_RUN column and extracting the stageRunId value from there.
 */
export function getStageRunId({
  evaluatedRow,
  rowData,
  columnDefinitions,
}: {
  evaluatedRow: Array<EvaluatedCell | {}> | null;
  rowData: RowData | null;
  columnDefinitions: Array<ColumnDefinition | null | undefined> | null;
}): string | null {
  const stageRunId = rowData && rowData.stageRunId;
  if (stageRunId != null) {
    return stageRunId;
  }
  // Column definitions are optional in a table and therefore may be null here.
  // Computed table may not be available yet and therefore evaluatedRow may be null.
  if (columnDefinitions == null || evaluatedRow == null) {
    return null;
  }
  const stageRunColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.STAGE_RUN,
    tableColumnDefinitions: columnDefinitions,
    isOptional: true,
  });
  if (stageRunColumnIndex < 0) {
    return null;
  }

  const stageRunCell = evaluatedRow[stageRunColumnIndex] as EvaluatedCell | null;
  if (
    stageRunCell == null ||
    stageRunCell.value == null ||
    stageRunCell.value.type !== BaseTypedValueType.STAGE_RUN
  ) {
    return null;
  }
  return stageRunCell.value.stageRunId;
}

export function getSampleSpecifierFromTable({
  evaluatedRow,
  rowData,
  columnDefinitions,
}: {
  evaluatedRow: Array<EvaluatedCell | {}> | null;
  rowData: RowData;
  columnDefinitions: Array<ColumnDefinition | null | undefined> | null;
}): SampleSpecifier | null {
  const {batchId, fileId, stageRunSampleId} = rowData;
  const stageRunId = getStageRunId({evaluatedRow, rowData, columnDefinitions});
  if (batchId) {
    return {batchId};
  } else if (fileId) {
    return {fileId};
  } else if (stageRunId || stageRunSampleId) {
    // Registration table rows may have a stage run but no sample.
    // Data table rows often have a sample but no stage run.
    // In any case, send both and let the server figure out which to use.
    return {stageRunId: stageRunId || null, stageRunSampleId: stageRunSampleId || null};
  } else {
    return null;
  }
}

/**
 * Transforms any FormulaParseErrors to include details about the
 * column where we had the error.  If there's an entry in
 * columnDefinitions for the particular column that we're looking at,
 * we'll use that displayName in the exceptions instead of just the
 * coordinates.
 *
 * If the exception received is not a FormulaParseError, then we just
 * return the error unmodified.
 */
function _addCellLocationToFormulaParseError(
  columnDefinitions: Array<ColumnDefinition | null | undefined> | null,
  i: number,
  j: number,
  ex: Error
): ExcessUtil.FormulaParseError | Error {
  if (ex instanceof ExcessUtil.FormulaParseError) {
    const cellLocation =
      columnDefinitions &&
      columnDefinitions[j] &&
      (columnDefinitions[j] as SchemaFieldColumnDefinition).schemaField &&
      (columnDefinitions[j] as SchemaFieldColumnDefinition).schemaField.displayName
        ? `${
            (columnDefinitions[j] as SchemaFieldColumnDefinition).schemaField.displayName
          } row ${ExcessUtil.rowIndexToNumber(i)}`
        : ExcessUtil.colNumberToLetters(j) + ExcessUtil.rowIndexToNumber(i);
    return new ExcessUtil.FormulaParseError(`Failed to parse formula in cell ${cellLocation}: ${ex.message}`);
  }
  return ex;
}

/**
 * Return array of LookupServerQuery to be used for refreshing lookup field values.
 */
export function getServerLookupQueries({
  minI,
  maxI,
  minJ,
  maxJ,
  table,
  computedTable,
  columnDefinitions,
}: MultiSelectionBox & {
  table: Table;
  computedTable?: Array<Array<EvaluatedCell>> | null;
  columnDefinitions: Array<ColumnDefinition | null | undefined> | null;
}): ServerLookupAggregatedQuery {
  const lookupSpecifiers = [];
  for (let i = minI; i <= maxI; i++) {
    if (table.rowData == null) {
      continue;
    }
    const sampleSpecifier = getSampleSpecifierFromTable({
      evaluatedRow: computedTable == null ? null : computedTable[i],
      rowData: table.rowData[i],
      columnDefinitions,
    });

    if (sampleSpecifier == null) {
      continue;
    }
    for (let j = minJ; j <= maxJ; j++) {
      try {
        for (const lookupSpecfier of getCellLookupSpecifiers(table.table[i][j])) {
          lookupSpecifiers.push({
            ...sampleSpecifier,
            ...lookupSpecfier,
            onlyValid: true,
          });
        }
      } catch (error) {
        throw _addCellLocationToFormulaParseError(columnDefinitions, i, j, error);
      }
    }
  }
  return lookupSpecifiers;
}

/**
 * Resolve ServerLookupResult as they are received from the server to the format that we
 * can store in the external cache of the table cell or render in Configure Lookup Field modal
 */
export function resolveSubQueryResults({
  serverLookupResult,
}: {
  serverLookupResult: ServerLookupResult;
}): Array<{
  subQueryLookupSpecifier: BaseLookupDefinition;
  subQueryLookupFieldResult: {
    lookupFieldDefinition: BaseLookupDefinition & SampleSpecifier;
    results: Array<LookupFieldResult>;
  };
}> {
  const subQueryResults = [];
  for (let i = 0; i < serverLookupResult.length; i++) {
    const subQueryResultData = serverLookupResult[i];
    const subQueryLookupSpecifier = subQueryResultData.lookupSpecifier;
    const subQuerySampleSpecifier = subQueryResultData.sampleSpecifier;
    const lookupFieldDefinition = normalizeLookupFieldDefinition({
      ...subQueryLookupSpecifier,
      ...subQuerySampleSpecifier,
    });
    const results = subQueryResultData.values
      .map((value: SubqueryLookupResultValue) => subQueryResultValueToLookupFieldResult(value))
      .filter((lookupFieldResult) => lookupFieldResult != null);
    const subQueryLookupFieldResult = {
      lookupFieldDefinition,
      results,
    };
    subQueryResults.push({subQueryLookupSpecifier: lookupFieldDefinition, subQueryLookupFieldResult});
  }

  return subQueryResults;
}

/**
 * Merge server result for an individual cell into the external cache
 */
export function mergeServerLookupResultIn({
  serverLookupResult,
  externalCache,
  rowSampleSpecifier,
}: {
  serverLookupResult: ServerLookupResult;
  externalCache: ExternalCache;
  rowSampleSpecifier: SampleSpecifier;
}): ExternalCache {
  let newExternalCache = externalCache;

  const resolvedSubQueryResults = resolveSubQueryResults({
    serverLookupResult,
  });

  for (let i = 0; i < resolvedSubQueryResults.length; i++) {
    const subQueryResult = resolvedSubQueryResults[i];
    const {subQueryLookupSpecifier, subQueryLookupFieldResult} = subQueryResult;

    let sampleSpecifier: SampleSpecifier;
    if (i === 0 && rowSampleSpecifier.stageRunSampleId != null) {
      // This is the first query in a workflow data table, the rowData contains stageRunSampleId,
      // but the server response contains batchId/fileId, we will assume that they are matching.
      // When we are rendering the value, we only have access to stageRunSampleId, therefore to
      // be able to interpret it correctly, we need to store the sampleSpecifier containing the
      // stageRunSampleId and not the batchId/fileId that we've got from the server.
      sampleSpecifier = normalizeSampleSpecifier(rowSampleSpecifier);
    } else {
      sampleSpecifier = subQueryLookupFieldResult.lookupFieldDefinition;
    }

    const lookupFieldResults = {
      ...pickLookupFieldDefinition({...subQueryLookupSpecifier, ...sampleSpecifier}),
      results: subQueryLookupFieldResult.results,
    };
    newExternalCache = mergeResultIntoExternalCache(newExternalCache, lookupFieldResults);
  }

  return newExternalCache;
}

/**
 * Fix results of LOOKUP_FIELD_FIELD_TYPE_EXP_COND to be represented as tag values similarly to
 * the other result types.
 *
 * TODO(BNCH-1935): We should update the server to return the results in the right format in the first place.
 * Then we wouldn't need this transformation anymore.
 */
export function getServerLookupResult({
  serverLookupQueries,
  serverLookupResults,
  cellLookupSpecifier,
  rowSampleSpecifier,
}: {
  serverLookupQueries: ServerLookupAggregatedQuery;
  serverLookupResults: ServerLookupAggregatedResult;
  cellLookupSpecifier: LookupSpecifier;
  rowSampleSpecifier: SampleSpecifier;
}): ServerLookupResult {
  assert(
    serverLookupQueries.length === serverLookupResults.length,
    'Number of results does not match the number of aggregated queries',
    {serverLookupQueries, serverLookupResults}
  );
  for (let i = 0; i < serverLookupQueries.length; i++) {
    const serverLookupQuery = serverLookupQueries[i];
    assert(
      serverLookupQuery != null && cellLookupSpecifier != null,
      `serverLookupQuery and cellLookupSpecifier are expected to exist.`
    );
    if (
      areLookupSpecifiersEqual(serverLookupQuery, cellLookupSpecifier) &&
      areSampleSpecifiersEqual(serverLookupQuery, rowSampleSpecifier)
    ) {
      if (serverLookupQuery.fieldType === LOOKUP_FIELD_FIELD_TYPE_EXP_COND) {
        assert(
          serverLookupQuery.fieldName != null,
          'Field name is expected in LOOKUPFIELD on experimental condition'
        );
        // Experimental conditions aren't tag values, so modify the last result
        const results = serverLookupResults[i];
        const intermediateResults = results.slice(0, -1);
        const finalResult = results[results.length - 1];
        const tagResult = {
          lookupSpecifier: finalResult.lookupSpecifier,
          sampleSpecifier: rowSampleSpecifier,
          values: [] as Array<SubqueryLookupResultValue>,
        };
        for (const strValue of finalResult.values) {
          assert(
            typeof strValue === 'string',
            'String value is expected in LOOKUPFIELD on experimental condition'
          );
          tagResult.values.push({
            tag: convertExterimentalConditionStringToTag(strValue, serverLookupQuery.fieldName),
          });
        }
        return [...intermediateResults, tagResult];
      } else {
        return serverLookupResults[i];
      }
    }
  }
  return [];
}

/**
 * Iterate over all the nodes with LOOKUPFIELD function. If the LOOKUPFIELD arguments are found in one
 * of the lookupSpecifiers, merge the corresponding serverLookupResult into the externalCache of the cell.
 */
function getUpdatedCell({
  cell,
  rowData,
  serverLookupQueries,
  serverLookupResults,
  evaluatedRow,
  columnDefinitions,
}: {
  cell: Cell;
  rowData: RowData | null;
  serverLookupQueries: ServerLookupAggregatedQuery;
  serverLookupResults: ServerLookupAggregatedResult;
  evaluatedRow: Array<EvaluatedCell> | null;
  columnDefinitions: Array<ColumnDefinition | null | undefined> | null;
}): Cell {
  if (rowData == null) {
    assert(false, 'Row data expected to exist when using lookup fields');
    return cell;
  }
  let newExternalCache: ExternalCache = {LOOKUPFIELD: []};
  const rowSampleSpecifier = getSampleSpecifierFromTable({evaluatedRow, rowData, columnDefinitions});

  if (rowSampleSpecifier == null) {
    return cell;
  }

  for (const cellLookupSpecifier of getCellLookupSpecifiers(cell)) {
    const serverLookupResult = getServerLookupResult({
      serverLookupQueries,
      serverLookupResults,
      cellLookupSpecifier,
      rowSampleSpecifier,
    });
    newExternalCache = mergeServerLookupResultIn({
      rowSampleSpecifier,
      serverLookupResult,
      externalCache: newExternalCache,
    });
  }
  return reactUpdate(cell, {$merge: {externalCache: newExternalCache}});
}

/**
 * Merge the results received from the server into the external cache of the cell.
 *
 * @param  lookupSpecifiers    - Array of Lookup Specifiers that were queried for. There is one element in
 *                               the array for each top level LOOKUPFIELD formula in a cell.
 * @param  serverLookupResults - Array of the same length as lookupSpecifiers. Each element represents the
 *                               results received from the server for the corresponding Lookup Specifier.
 */
export function mergeServerLookupResultsIntoCells({
  minI,
  maxI,
  minJ,
  maxJ,
  table,
  serverLookupQueries,
  serverLookupResults,
  computedTable,
  columnDefinitions,
}: MultiSelectionBox & {
  table: Table;
  serverLookupQueries: ServerLookupAggregatedQuery;
  serverLookupResults: ServerLookupAggregatedResult;
  computedTable?: Array<Array<EvaluatedCell>>;
  columnDefinitions?: Array<ColumnDefinition | null | undefined> | null;
}): Array<Array<Cell>> {
  assert(
    serverLookupQueries.length === serverLookupResults.length,
    'Number of results does not match the number of aggregated queries',
    {serverLookupQueries, serverLookupResults}
  );

  return table.table.map((row, i) =>
    row.map((cell, j) =>
      i < minI || i > maxI || j < minJ || j > maxJ
        ? cell
        : getUpdatedCell({
            cell,
            rowData: table.rowData == null ? null : table.rowData[i],
            serverLookupQueries,
            serverLookupResults,
            evaluatedRow: computedTable == null ? null : computedTable[i],
            columnDefinitions: columnDefinitions || null,
          })
    )
  );
}

/**
 * Update lookup field values for a range of cells.
 */
export async function handleUpdateLookupFieldValues({
  minI,
  maxI,
  minJ,
  maxJ,
  table,
  computedTable,
  columnDefinitions,
  onUpdateCells,
}: MultiSelectionBox & {
  table: Table;
  computedTable?: Array<Array<EvaluatedCell>>;
  columnDefinitions: Array<ColumnDefinition | null | undefined> | null;
  onUpdateCells: (cells: Array<Array<Cell>>) => void;
}): Promise<void> {
  let serverLookupQueries = [];
  try {
    serverLookupQueries = getServerLookupQueries({
      minI,
      maxI,
      minJ,
      maxJ,
      table,
      computedTable,
      columnDefinitions,
    });
  } catch (e) {
    if (e instanceof ExcessUtil.FormulaParseError) {
      notifications.error(e.message);
      return;
    }
    throw e;
  }

  if (serverLookupQueries.length === 0) {
    return;
  }

  const {data, errors} = await withLoadingScreen(SearchDAO.lookupFieldValues(serverLookupQueries));
  if (errors.length > 0) {
    for (const error of errors) {
      notifications.error(error);
    }
  }
  if (data != null) {
    assert(
      data.length === serverLookupQueries.length,
      'The number of results does not match the number of lookup specifiers'
    );
    onUpdateCells(
      mergeServerLookupResultsIntoCells({
        minI,
        maxI,
        minJ,
        maxJ,
        table,
        serverLookupQueries,
        serverLookupResults: data,
        computedTable,
        columnDefinitions,
      })
    );
  }
}
